import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { ActionIcon, Box, Button, Drawer, Flex, Group, LoadingOverlay, Select, Stack, Text } from '@mantine/core';
import { DatePickerInput, TimeInput } from '@mantine/dates';
import { Practitioner, PractitionerRole, Reference, Schedule, Slot } from '@medplum/fhirtypes';
import { useEffect, useMemo, useState } from 'react';
import { IconX } from '@tabler/icons-react';
import { alert } from '@/services/alert';
import { fetchBot } from '@/services/services';
import { RichTextInput } from '@/components/RichTextInput/RichTextInput';
import { useMedplum } from '@medplum/react';
import { createReference, getReferenceString } from '@medplum/core';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  BookCalendarBlockRequest,
  BookCalendarBlockResult,
  BookCalendarBlockSeriesResult,
} from '@bariendo/lib/scheduling';
import { AppointmentRecurrencePreset, PractitionerWithRole } from '../types';
import { PractitionerInput } from '@/components/PractitionerInput';
import { TimezoneInput } from '../components/TimezoneInput';
import { useLocationTimezone } from '../hooks/useLocationTimezone';
import { getBrowserTimezone } from '../utils/common';
import {
  buildBlockRecurrenceFromForm,
  computeMonthlyNthWeekdayFromDate,
  DEFAULT_MONTHLY_RECURRENCE_WEEKDAY,
  nextBlockFormDataWithRecurrencePreset,
  toOrdinal,
} from '../utils/appointment';
import { BlockDrawerProps, BlockFormData } from './types';
import { INITIAL_BLOCK_FORM_DATA, REPEAT_TIMES_OPTIONS } from './blockDrawer.constants';
import { getUUID, parseSlotComment } from './blockDrawer.utils';

dayjs.extend(utc);
dayjs.extend(timezone);

async function resolveProviderForSlot(
  medplum: ReturnType<typeof useMedplum>,
  slot: Slot,
  practitioners: PractitionerWithRole[] | undefined,
  practitionerRoles: PractitionerRole[] | undefined
): Promise<PractitionerWithRole | null> {
  const schedRef = slot.schedule;
  if (!schedRef?.reference) return null;
  const schedule = await medplum.readReference(schedRef as Reference<Schedule>);
  const actorRef = schedule.actor?.[0]?.reference;
  if (!actorRef || !practitionerRoles?.length) return null;
  const role = practitionerRoles.find((r) => getReferenceString(r) === actorRef);
  const pref = role?.practitioner?.reference;
  if (!pref || !practitioners?.length) return null;
  return practitioners.find((p) => getReferenceString(p) === pref || p.id === pref.split('/').pop()) ?? null;
}

export function BlockDrawer({
  opened,
  onClose,
  practitioners,
  practitionerRoles,
  slotReference,
  detachFromSeries,
  onSaveSuccess,
}: BlockDrawerProps) {
  const medplum = useMedplum();
  const { currentUser } = useCurrentUser();

  const currentProvider = useMemo(() => {
    if (!currentUser || currentUser.resourceType !== 'Practitioner') return null;
    return practitioners?.find((p) => p.id === (currentUser as Practitioner).id) ?? null;
  }, [currentUser, practitioners]);

  const { timezoneSuffix, resolveTimezoneSelection, reset: resetTimezoneSuffix } = useLocationTimezone();

  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingSlot, setIsLoadingSlot] = useState(false);
  const [formData, setFormData] = useState<BlockFormData>(INITIAL_BLOCK_FORM_DATA);

  const isValidTimeString = (value: string) => /^([01]\d|2[0-3]):[0-5]\d$/.test(value);

  const isTimeRangeValid = (startTime: string, endTime: string) => {
    if (!formData.date || !formData.timezone) return false;
    if (!isValidTimeString(startTime) || !isValidTimeString(endTime)) return false;

    const dateStr = dayjs(formData.date).format('YYYY-MM-DD');
    const start = dayjs.tz(`${dateStr}T${startTime}:00`, formData.timezone);
    const end = dayjs.tz(`${dateStr}T${endTime}:00`, formData.timezone);

    return end.isAfter(start);
  };

  const handleTimeFieldChange = (key: 'startTime' | 'endTime') => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => {
      const nextValue = event.currentTarget?.value ?? '';
      return { ...prev, [key]: nextValue };
    });
  };

  const validateFormData = (data: BlockFormData): boolean => {
    if (data.provider === null) return false;
    if (data.date === null) return false;
    if (data.timezone === null) return false;
    if (!isValidTimeString(data.startTime)) return false;
    if (!isValidTimeString(data.endTime)) return false;
    if (!isTimeRangeValid(data.startTime, data.endTime)) return false;
    if (data.isRepeating && (data.repeatTimes < 1 || data.repeatTimes > 52)) return false;
    return true;
  };

  const isFormValid = validateFormData(formData);

  const handleTimezoneChange = (value: string) => {
    resolveTimezoneSelection(value);
    setFormData((prev) => ({ ...prev, timezone: value }));
  };

  const handleRepeatTimesChange = (value: string | null) => {
    if (!value) return;
    setFormData((prev) => ({
      ...prev,
      repeatTimes: Number(value),
    }));
  };

  const handleDateChange = (value: string | null) => {
    setFormData((prev) => {
      const nextDate = value ? dayjs(value).toDate() : null;
      if (!nextDate && prev.recurrencePreset === 'monthly') {
        return {
          ...prev,
          date: null,
          recurrencePreset: 'none',
          isRepeating: false,
        };
      }
      return { ...prev, date: nextDate };
    });
  };

  const handleNotesChange = (text: string, json: object | null) => {
    setFormData((prev) => ({ ...prev, notes: text, notesJson: json }));
  };

  const handleProviderChange = (resource: PractitionerWithRole | null) => {
    setFormData((prev) => ({ ...prev, provider: resource }));
  };

  useEffect(() => {
    if (formData.recurrencePreset !== 'monthly' || !formData.date || !formData.timezone) {
      return;
    }
    const { n, weekday } = computeMonthlyNthWeekdayFromDate(formData.date, formData.timezone);
    setFormData((prev) => {
      if (prev.recurrencePreset !== 'monthly') {
        return prev;
      }
      if (prev.monthlyNth === n && prev.monthlyWeekday === weekday) {
        return prev;
      }
      return { ...prev, monthlyNth: n, monthlyWeekday: weekday };
    });
  }, [formData.date, formData.timezone, formData.recurrencePreset]);

  const recurrenceSelectData = useMemo(() => {
    const tz = formData.timezone ?? 'UTC';
    const base = [
      { value: 'none', label: 'Does not repeat' },
      { value: 'daily', label: 'Daily' },
      { value: 'weekly', label: 'Weekly' },
      { value: 'biweekly', label: 'Every 2 weeks' },
    ];
    if (!formData.date) {
      return [...base, { value: 'monthly', label: 'Monthly on the… (select a date)', disabled: true }];
    }
    const { n } = computeMonthlyNthWeekdayFromDate(formData.date, tz);
    const weekdayName = dayjs(formData.date).tz(tz).format('dddd');
    return [...base, { value: 'monthly', label: `Monthly on the ${toOrdinal(n)} ${weekdayName}` }];
  }, [formData.date, formData.timezone]);

  const handleRecurrencePresetChange = (value: string | null) => {
    if (!value) {
      return;
    }
    const tz = formData.timezone ?? 'UTC';
    setFormData((prev) => nextBlockFormDataWithRecurrencePreset(prev, value as AppointmentRecurrencePreset, tz));
  };

  useEffect(() => {
    const loadExistingBlockSlot = async () => {
      if (!opened) return;

      if (!slotReference) {
        setFormData({ ...INITIAL_BLOCK_FORM_DATA, provider: currentProvider });
        resetTimezoneSuffix();
        return;
      }

      setIsLoadingSlot(true);
      try {
        const slot = await medplum.readReference({ reference: slotReference } as Reference<Slot>);
        if (!slot?.start || !slot.end) return;

        const provider =
          (await resolveProviderForSlot(medplum, slot, practitioners, practitionerRoles)) ?? currentProvider ?? null;
        const tz = getBrowserTimezone();
        const start = dayjs(slot.start).tz(tz);
        const end = dayjs(slot.end).tz(tz);
        const { notes, notesJson } = parseSlotComment(slot.comment);

        setFormData({
          provider,
          timezone: tz,
          date: start.toDate(),
          startTime: start.format('HH:mm'),
          endTime: end.format('HH:mm'),
          isRepeating: false,
          recurrencePreset: 'none',
          repeatTimes: 1,
          monthlyNth: 1,
          monthlyWeekday: DEFAULT_MONTHLY_RECURRENCE_WEEKDAY,
          notes,
          notesJson,
        });
        resolveTimezoneSelection(tz);
      } finally {
        setIsLoadingSlot(false);
      }
    };

    void loadExistingBlockSlot();
  }, [
    slotReference,
    currentProvider,
    medplum,
    opened,
    practitioners,
    practitionerRoles,
    resetTimezoneSuffix,
    resolveTimezoneSelection,
  ]);

  const handleClose = () => {
    setFormData(INITIAL_BLOCK_FORM_DATA);
    resetTimezoneSuffix();
    onClose();
  };

  const handleSave = async () => {
    if (isSaving || !isFormValid) return;

    if (!formData.provider || !formData.date || !formData.timezone || !formData.startTime) {
      alert.error('Please fill in all required fields');
      return;
    }

    setIsSaving(true);

    try {
      const date = dayjs(formData.date).format('YYYY-MM-DD');
      const payload: BookCalendarBlockRequest = {
        practitioner: createReference(formData.provider),
        date,
        startTime: formData.startTime,
        endTime: formData.endTime,
        timezone: formData.timezone,
        ...(slotReference && detachFromSeries ? { detachFromSeries: true } : {}),
        ...(formData.isRepeating && !slotReference
          ? {
              seriesId: getUUID(),
              recurrence: buildBlockRecurrenceFromForm(formData),
            }
          : {}),
        notes: formData.notes || undefined,
        richTextNotes: formData.notesJson ? JSON.stringify(formData.notesJson) : undefined,
        ...(slotReference ? { slot: { reference: slotReference } as Reference<Slot> } : {}),
      };

      const result = await fetchBot<BookCalendarBlockResult | BookCalendarBlockSeriesResult>(
        medplum,
        'book-calendar-block',
        payload
      );

      if ('results' in result && result.results) {
        for (const r of result.results) {
          for (const warning of r.warnings ?? []) {
            alert.warning(warning.message);
          }
        }
      } else if ('warnings' in result && result.warnings) {
        for (const warning of result.warnings) {
          alert.warning(warning.message);
        }
      }

      onSaveSuccess?.();
      handleClose();
    } catch (error) {
      alert.error(`Couldn't save block: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={handleClose}
      position="right"
      size="sm"
      padding="md"
      styles={{
        header: { padding: '0 16px' },
        body: { padding: 0, height: '100vh', display: 'flex', flexDirection: 'column' },
        content: { display: 'flex', flexDirection: 'column' },
      }}
      withCloseButton={false}
    >
      <Drawer.Header>
        <Group justify="space-between" w="100%" align="center">
          <Text fw={600} size="lg">
            Block
          </Text>
          <ActionIcon variant="subtle" color="gray" size="sm" onClick={handleClose}>
            <IconX size={18} />
          </ActionIcon>
        </Group>
      </Drawer.Header>

      <Flex direction="column" style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        <LoadingOverlay visible={isLoadingSlot} zIndex={10} overlayProps={{ blur: 2 }} />
        <Box style={{ flex: 1, overflow: 'auto', paddingTop: 16, paddingLeft: 16, paddingRight: 16 }}>
          <Stack gap="md">
            <Box>
              <PractitionerInput
                value={formData.provider}
                onChange={handleProviderChange}
                practitioners={practitioners}
                disabled={Boolean(slotReference)}
              />
            </Box>

            <TimezoneInput
              value={formData.timezone ?? ''}
              onChange={handleTimezoneChange}
              displaySuffix={timezoneSuffix}
            />

            <Box>
              <Text size="md" fw={500} c="gray.7" mb="xs">
                Date and time
              </Text>

              <Box mb="sm">
                <DatePickerInput
                  required
                  label="Date"
                  placeholder="Select date"
                  value={formData.date}
                  onChange={handleDateChange}
                  valueFormat="MMMM D, YYYY"
                  minDate={new Date()}
                />
              </Box>

              <Group grow>
                <Box>
                  <TimeInput
                    required
                    label="Start time"
                    value={formData.startTime}
                    onChange={handleTimeFieldChange('startTime')}
                  />
                </Box>

                <Box>
                  <TimeInput
                    required
                    label="End time"
                    value={formData.endTime}
                    onChange={handleTimeFieldChange('endTime')}
                  />
                </Box>
              </Group>

              {!slotReference && (
                <>
                  <Select
                    mt="sm"
                    label="Recurrence"
                    data={recurrenceSelectData}
                    value={formData.recurrencePreset}
                    onChange={handleRecurrencePresetChange}
                  />
                  <Select
                    mt="sm"
                    label="Ends after (occurrences)"
                    data={REPEAT_TIMES_OPTIONS}
                    value={String(formData.repeatTimes)}
                    disabled={formData.recurrencePreset === 'none'}
                    onChange={handleRepeatTimesChange}
                  />
                </>
              )}
            </Box>

            <Box pb="md">
              <Text size="md" fw={500} c="gray.7" mb="xs">
                Notes
              </Text>
              <RichTextInput
                value={formData.notesJson ?? formData.notes}
                onChange={handleNotesChange}
                placeholder="Write Appointment Notes"
                contentMinHeight="100px"
                contentMaxHeight="200px"
              />
            </Box>
          </Stack>
        </Box>

        <Box
          px="md"
          py="sm"
          style={{
            borderTop: '1px solid var(--mantine-color-gray-3)',
            backgroundColor: 'var(--mantine-color-body)',
          }}
        >
          <Group justify="flex-end">
            <Button
              radius="xl"
              color="#228BE6"
              disabled={!isFormValid || isSaving}
              loading={isSaving}
              onClick={handleSave}
            >
              Save
            </Button>
          </Group>
        </Box>
      </Flex>
    </Drawer>
  );
}
